import { useNavigation, useRoute } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { TouchableOpacity } from 'react-native-gesture-handler';

import { Alert } from '@components/Alert';
import { NativeConfirmModal } from '@components/Modals/NativeConfirmModal';
import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { MemberList } from '@components/User/MemberList';
import {
  User,
  useAssignTeamOwnerMutation,
  useLeaveTeamMutation,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useTeamFromQuery } from '@hooks/useTeamFromQuery';
import { TeamsStackScreenProps } from '@navigation/teams/teams-stack';
import { removeTeamFromCache } from '@utils/teams/removeTeamFromCache';

export const AssignNewOwner: React.FC = () => {
  const navigation = useNavigation();
  const { me } = useMe();
  const { t } = useTranslation();

  const [showAssignConfirmBox, setShowAssignConfirmBox] = useState(false);
  const [assignUser, setAssignUser] = useState<User | undefined>(undefined);
  // Copilot review fix (PR #5799): `assignTeamOwnerLoading || leaveTeamLoading`
  // alone can go false for a render or two between assignTeamOwner
  // resolving and the effect below actually starting leaveTeam (effects run
  // after the render that updates assignTeamOwnerData/assignTeamOwnerLoading
  // together) — unlocking the modal mid-sequence. This flag stays true for
  // the whole confirm→assign→leave chain regardless of that gap.
  const [isTransferringOwnership, setIsTransferringOwnership] = useState(false);

  const route = useRoute<TeamsStackScreenProps<'assign-new-owner'>['route']>();

  const {
    params: { teamUUId },
  } = route || { teamUUId: '' };

  const { team, loading } = useTeamFromQuery({
    teamUUId,
  });

  const [teamId, setTeamId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (loading && !team?.id) {
      setIsLoading(true);
      return;
    }
    setIsLoading(false);
    team?.id && setTeamId(team?.id);
  }, [team?.id, loading]);

  const [
    leaveTeam,
    { data: leaveTeamData, error: leaveTeamError, loading: leaveTeamLoading },
  ] = useLeaveTeamMutation({
    // Same cache cleanup as the web leave/delete flows (TA-10471) — drops
    // every cached listTeams variant AND evicts the Team entity, so a
    // later cache-first getTeam read can't serve stale data for a team
    // just left.
    update(cache, _result, { variables }) {
      removeTeamFromCache(cache, variables?.id);
    },
  });

  useEffect(() => {
    if (!leaveTeamData) return;
    navigation.popToTop();
  }, [leaveTeamData]);

  const [
    assignTeamOwner,
    {
      data: assignTeamOwnerData,
      error: assignTeamOwnerError,
      loading: assignTeamOwnerLoading,
    },
  ] = useAssignTeamOwnerMutation({
    // Code review fix: assignTeamOwnerError (below) already drives an
    // unconditional Alert for any error — opt out of the global
    // PERMISSION_DENIED/CONFLICT toast so this doesn't double up. Only this
    // call site opts out; TeamDetails.web.tsx's assignTeamOwner call only
    // console.logs and still needs the global toast.
    context: { suppressGlobalMutationErrorToast: true },
  });

  useEffect(() => {
    if (!assignTeamOwnerData) return;
    if (!teamId) return;
    leaveTeam({ variables: { id: teamId } });
  }, [assignTeamOwnerData]);

  useEffect(() => {
    if (!assignTeamOwnerError) return;
    setIsTransferringOwnership(false);
    Alert.alert(t('shared:error'), assignTeamOwnerError.message);
  }, [assignTeamOwnerError]);

  useEffect(() => {
    // Copilot review fix (PR #5799): ownership was already transferred by
    // the time leaveTeam runs — if leaveTeam itself fails,
    // isTransferringOwnership must still clear (and surface the error) or
    // the modal stays locked forever with no way to retry/cancel.
    if (!leaveTeamError) return;
    setIsTransferringOwnership(false);
    Alert.alert(t('shared:error'), leaveTeamError.message);
  }, [leaveTeamError]);

  const { users } = team;

  const { id: myId } = me || { id: '' };
  const teamMembersWithoutMe = users?.filter((u) => u.id !== myId);

  const assignNewOwner = (user: User) => {
    if (!teamId) return;

    setIsTransferringOwnership(true);
    assignTeamOwner({
      variables: {
        id: teamId,
        newOwnerId: user.id,
      },
    });
  };

  // TA-10392: native used to confirm via `Alert.alert`, which can't host a
  // spinner or lock its buttons while `assignTeamOwner` (then `leaveTeam`)
  // is in flight. Native now shares the same `showAssignConfirmBox`/
  // `assignUser` state as the web WebModal below, rendering
  // `NativeConfirmModal` instead — see its render for how `loading` covers
  // the whole assign-then-leave sequence.
  const assignNewOwnerAlert = (user: User) => {
    setAssignUser(user);
    setShowAssignConfirmBox(true);
  };

  if (isLoading) {
    return <ActivityIndicatorLoading />;
  }

  if (!team) {
    return (
      <Box flex={1} paddingTop='m' alignItems='center'>
        <Text>Team not found</Text>
      </Box>
    );
  }

  return (
    <Box flex={1}>
      <MemberList
        onPress={(user) => assignNewOwnerAlert(user)}
        users={teamMembersWithoutMe || []}
        listEmptyComponent={
          <Box marginHorizontal='m' mt='l'>
            <Text textAlign='center' color='grey04'>
              {t('models:teams.assignNewOwner.noOtherMembers')}
            </Text>
          </Box>
        }
      />
      {Platform.OS === 'web' && (
        <WebModal
          marginTop='m'
          visible={showAssignConfirmBox}
          width={483}
          height={196}
          title={t('models:teams.leave.title')}
          titleVariant='labelLarge'
          titleColor='textSecondary'
          marginHorizontal='l'
          onClose={() => {
            setShowAssignConfirmBox(false);
          }}>
          <Box flex={1} mt='l' mx='l'>
            <Text variant='webBodySecondary' color='black'>
              {t('models:teams.assignNewOwner.messageDowngradedToAdmin', {
                member: assignUser?.name,
              })}
            </Text>
            <Box
              mt='l'
              flexDirection='row'
              alignItems='flex-end'
              justifyContent='flex-end'>
              <TouchableOpacity
                onPress={() => {
                  setShowAssignConfirmBox(false);
                }}>
                <Box
                  height={56}
                  justifyContent='center'
                  alignContent='center'
                  alignItems='center'
                  accessibilityLabel={t(
                    'models:teams.removeTeamMember.assign'
                  )}>
                  <Text variant='typography' color='textPrimary' mr='l'>
                    {t('shared:cancel')}
                  </Text>
                </Box>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => {
                  setShowAssignConfirmBox(false);
                  assignNewOwner(assignUser!);
                }}>
                <Box
                  backgroundColor='black'
                  borderRadius='xs'
                  width={100}
                  height={56}
                  justifyContent='center'
                  alignContent='center'
                  alignItems='center'
                  accessibilityLabel={t(
                    'models:teams.removeTeamMember.assign'
                  )}>
                  <Text variant='typography' color='white'>
                    {t('models:teams.removeTeamMember.assign')}
                  </Text>
                </Box>
              </TouchableOpacity>
            </Box>
          </Box>
        </WebModal>
      )}
      {Platform.OS !== 'web' && (
        <NativeConfirmModal
          visible={showAssignConfirmBox}
          onCancel={() => setShowAssignConfirmBox(false)}
          onConfirm={() => assignUser && assignNewOwner(assignUser)}
          loading={
            isTransferringOwnership ||
            assignTeamOwnerLoading ||
            leaveTeamLoading
          }
          title={t('models:teams.assignNewOwner.title')}
          // This flow (unlike TeamMembers.tsx's) chains straight into
          // `leaveTeam` on success, so the acting user leaves the team
          // entirely rather than staying on as admin — keep the original
          // native copy (`.message`), not the web modal's
          // `.messageDowngradedToAdmin`, which doesn't apply here.
          message={t('models:teams.assignNewOwner.message', {
            member: assignUser?.name,
          })}
          confirmLabel={t('shared:assign')}
          cancelLabel={t('shared:cancel')}
        />
      )}
    </Box>
  );
};
